import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { LANGUAGE_STORAGE_KEY } from "../i18n";

const LANGUAGES = ["ru", "en", "uz"];

function currentLanguage(i18n) {
  const code = i18n.language?.split("-")[0];
  return LANGUAGES.includes(code) ? code : "ru";
}

function readUrlPreferences() {
  try {
    const params = new URLSearchParams(window.location.search);
    const theme = params.get("theme");
    const language = params.get("lang");
    return {
      theme: theme === "light" || theme === "dark" ? theme : null,
      language: LANGUAGES.includes(language) ? language : null,
    };
  } catch {
    return { theme: null, language: null };
  }
}

export default function AccountPreferenceSync() {
  const { session } = useAuth();
  const { theme, setTheme } = useTheme();
  const { i18n } = useTranslation();
  const themeRef = useRef(theme);
  const languageRef = useRef(currentLanguage(i18n));
  const skipWrite = useRef(false);
  const localChangeAt = useRef(0);
  const urlLock = useRef(readUrlPreferences());
  themeRef.current = theme;
  languageRef.current = currentLanguage(i18n);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has("theme") && !params.has("lang")) return;
    params.delete("theme");
    params.delete("lang");
    const query = params.toString();
    window.history.replaceState(null, "", `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`);
  }, []);

  useEffect(() => {
    if (!session?.user || !supabase) return undefined;

    const applyRemote = (metadata) => {
      if (!metadata || Date.now() - localChangeAt.current < 4000) return;
      const lock = urlLock.current;
      const nextTheme = metadata.theme === "light" || metadata.theme === "dark" ? metadata.theme : null;
      const nextLanguage = LANGUAGES.includes(metadata.language) ? metadata.language : null;
      const themeMatchesLock = !lock.theme || nextTheme === lock.theme;
      const languageMatchesLock = !lock.language || nextLanguage === lock.language;
      if (themeMatchesLock && languageMatchesLock) urlLock.current = { theme: null, language: null };

      const themeChanged = nextTheme && nextTheme !== themeRef.current && !lock.theme;
      const languageChanged = nextLanguage && nextLanguage !== languageRef.current && !lock.language;
      if (!themeChanged && !languageChanged) return;
      skipWrite.current = true;
      if (themeChanged) setTheme(nextTheme);
      if (languageChanged) {
        try {
          window.localStorage.setItem(LANGUAGE_STORAGE_KEY, nextLanguage);
        } catch {
          // The language still changes for this session if storage is unavailable.
        }
        i18n.changeLanguage(nextLanguage);
      }
    };

    applyRemote(session.user.user_metadata);
    const pull = async () => {
      const { data } = await supabase.auth.getUser();
      applyRemote(data?.user?.user_metadata);
    };
    const timer = window.setInterval(pull, 8000);
    return () => window.clearInterval(timer);
  }, [i18n, session?.user, setTheme]);

  useEffect(() => {
    if (skipWrite.current) {
      skipWrite.current = false;
      return undefined;
    }
    if (!session?.user || !supabase) return undefined;
    const language = currentLanguage(i18n);
    const metadata = session.user.user_metadata || {};
    if (metadata.theme === theme && metadata.language === language) return undefined;
    localChangeAt.current = Date.now();
    const timer = window.setTimeout(() => {
      supabase.auth.updateUser({ data: { theme, language } }).catch(() => {});
    }, 300);
    return () => window.clearTimeout(timer);
  }, [i18n, session?.user, theme]);

  return null;
}
